/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getGoogleLoginUrl, getMicrosoftLoginUrl, getAppleLoginUrl, loginEmail, requestPasswordReset, resendVerification, ApiError } from '../services';
import './Login.css';
import { safeReturnTo } from '../utils/returnTo';
import { markAuthHandoff } from '../utils/authHandoff';

/** How long the "Sent" toast stays up on the email-not-confirmed screen. */
const SENT_TOAST_MS = 4000;

/**
 * An error box (Almanac S9a, decision 7): a brick border and a brick "!"
 * mark carry the alarm; the words stay ink.
 */
const ErrorBox: React.FC<{ children: React.ReactNode; testId?: string }> = ({ children, testId }) => (
  <div className="login-err" role="alert" data-testid={testId}>
    <span className="login-err-mark" aria-hidden="true">!</span>
    <p>{children}</p>
  </div>
);

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'login' | 'forgot' | 'unverified'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  // The server answered "that email and password don't match" (401).
  const [wrongCredentials, setWrongCredentials] = useState(false);
  const [sentToast, setSentToast] = useState(false);
  // The address the unverified answer was for, captured when the sign-in was
  // sent: the screen names it and the resend goes to it, whatever the field
  // holds by the time the answer arrives.
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  // Ownership of the page's async answers. Each request (sign-in, resend,
  // reset) takes a ticket; switching mode or starting another request takes a
  // newer one, and an answer whose ticket is no longer current changes nothing
  // on the screen.
  const ticketRef = useRef(0);
  // Set once a sign-in succeeds, just before the page navigates away. From
  // then on the screen is terminal: no ticket owns it (not even one taken
  // later), and no mode switch or submit changes anything or starts a request.
  // Navigation is not synchronous, so without this a later attempt's failure
  // could still paint an error over a sign-in that already worked.
  const completedRef = useRef(false);
  const takeTicket = () => {
    ticketRef.current += 1;
    return ticketRef.current;
  };
  const owns = (ticket: number) => !completedRef.current && ticket === ticketRef.current;

  // Check for error from OAuth redirect
  const oauthError = searchParams.get('error');

  useEffect(() => {
    if (!sentToast) return undefined;
    const timer = setTimeout(() => setSentToast(false), SENT_TOAST_MS);
    return () => clearTimeout(timer);
  }, [sentToast]);

  // A completed sign-in leaves this page terminal (completedRef). If the
  // browser's Back restores it from the back/forward cache, React does not
  // remount: the form would stay frozen and App would still think no one is
  // signed in. Reload instead, so App's mount-time auth check reconciles the
  // live session. An ordinary restore of an untouched page is left alone.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && completedRef.current) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const handleGoogleLogin = () => {
    window.location.href = getGoogleLoginUrl();
  };

  const handleMicrosoftLogin = () => {
    window.location.href = getMicrosoftLoginUrl();
  };

  const handleAppleLogin = () => {
    window.location.href = getAppleLoginUrl();
  };

  const showMode = (next: 'login' | 'forgot' | 'unverified') => {
    if (completedRef.current) return;
    // Whatever was in flight no longer speaks for this screen.
    takeTicket();
    setLoading(false);
    setError('');
    setMessage('');
    setWrongCredentials(false);
    setSentToast(false);
    setMode(next);
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (completedRef.current) return;
    const ticket = takeTicket();
    const submittedEmail = email;
    setError('');
    setWrongCredentials(false);
    setLoading(true);

    try {
      await loginEmail(submittedEmail, password);
      // A success always completes, even if the person moved to another
      // screen meanwhile: the server has set the session cookie, and only a
      // page load brings App's auth state in line with it. Only failures are
      // ticket-guarded. The success does end the screen: it retires every
      // ticket, so no other answer or click can change what is on it.
      completedRef.current = true;
      markAuthHandoff();
      takeTicket();
      // Refresh the page to trigger auth check in App.tsx, returning to
      // wherever the user was sent from — signing in to keep a guide should
      // land back on that guide, not on Today. Validated: the value is
      // attacker-controlled and goes into window.location.
      window.location.href = safeReturnTo();
    } catch (err: unknown) {
      if (!owns(ticket)) return;
      if (err instanceof ApiError && err.body.requires_verification === true) {
        // The password verified against an inactive account (CLAUDE.md Auth):
        // the account exists and is waiting on its email link.
        showMode('unverified');
        setUnverifiedEmail(submittedEmail);
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error && err.message === 'Unauthorized') {
        // fetchApi turns the 401 "Invalid email or password" into a bare
        // Unauthorized error; say it the design's way, inline.
        setWrongCredentials(true);
      } else {
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    } finally {
      if (owns(ticket)) setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (completedRef.current) return;
    const ticket = takeTicket();
    setError('');
    setSentToast(false);
    setLoading(true);
    try {
      const response = await resendVerification(unverifiedEmail);
      if (!owns(ticket)) return;
      // Resend reports whether the provider took the email (CLAUDE.md Auth):
      // only an accepted send is "Sent".
      if (response.email_sent === false) {
        setError(response.message || 'We couldn’t send the link. Try again in a minute.');
      } else {
        setSentToast(true);
      }
    } catch (err: unknown) {
      if (!owns(ticket)) return;
      setError(err instanceof Error ? err.message : 'We couldn’t send the link. Try again in a minute.');
    } finally {
      if (owns(ticket)) setLoading(false);
    }
  };

  const handleUseDifferentEmail = () => {
    if (completedRef.current) return;
    setEmail('');
    setPassword('');
    showMode('login');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (completedRef.current) return;
    const ticket = takeTicket();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await requestPasswordReset(email);
      if (!owns(ticket)) return;
      setMessage(response.message);
    } catch (err: unknown) {
      if (!owns(ticket)) return;
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      if (owns(ticket)) setLoading(false);
    }
  };

  const backToSignIn = (
    <button type="button" className="login-back-link" onClick={() => showMode('login')}>
      ‹ Sign in
    </button>
  );

  if (mode === 'unverified') {
    return (
      <div className="login-page">
        <div className="login-container">
          {backToSignIn}
          <h1 className="login-title">Confirm your email first</h1>
          <p className="login-body">
            Your password is right. We sent a link to <b>{unverifiedEmail}</b> when you signed up. Open it, then sign in.
          </p>

          {error && <ErrorBox>{error}</ErrorBox>}

          <button
            type="button"
            className="login-button email-login"
            onClick={handleResendVerification}
            disabled={loading}
          >
            {loading ? 'Sending…' : 'Send the link again'}
          </button>
          <div className="login-footer">
            <button type="button" className="link-button" onClick={handleUseDifferentEmail}>
              Use a different email
            </button>
          </div>

          {sentToast && (
            <div className="login-toast" role="status">Sent. Check spam too.</div>
          )}
        </div>
      </div>
    );
  }

  if (mode === 'forgot') {
    return (
      <div className="login-page">
        <div className="login-container">
          {backToSignIn}
          <h1 className="login-title">Reset Password</h1>

          {error && <ErrorBox>{error}</ErrorBox>}
          {message && <div className="login-note" role="status">{message}</div>}

          <form onSubmit={handleForgotPassword} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>

            <button type="submit" className="login-button email-login" disabled={loading}>
              {loading ? 'Sending…' : 'Send reset link'}
            </button>
          </form>

          <p className="login-fine">
            The link opens vizday.com, where you choose a new password. Then come back here and sign in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <Link to="/" className="login-back-link" aria-label="Back">‹</Link>
        <h1 className="login-title">Welcome back</h1>

        {oauthError && <ErrorBox>Authentication failed. Please try again.</ErrorBox>}

        {/* OAuth Buttons. The brand marks keep their own colours: they are
            third-party logos, not theme colour. */}
        <div className="oauth-buttons">
          <button className="login-button apple-login" onClick={handleAppleLogin}>
            <svg className="oauth-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Continue with Apple
          </button>

          <button className="login-button google-login" onClick={handleGoogleLogin}>
            <svg className="oauth-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <button className="login-button microsoft-login" onClick={handleMicrosoftLogin}>
            <svg className="oauth-icon" viewBox="0 0 21 21">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            Continue with Microsoft
          </button>
        </div>

        <div className="login-divider">
          <span>Or email</span>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleEmailLogin} className="login-form">
          <div className={`form-group${wrongCredentials ? ' bad' : ''}`}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              aria-invalid={wrongCredentials || undefined}
              required
            />
          </div>

          <div className={`form-group${wrongCredentials ? ' bad' : ''}`}>
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              aria-invalid={wrongCredentials || undefined}
              required
            />
          </div>

          {wrongCredentials && (
            <ErrorBox testId="login-wrong-credentials">
              <strong>That email and password don’t match.</strong> Check both, or{' '}
              <button type="button" className="link-button" onClick={() => showMode('forgot')}>
                reset the password
              </button>
              .
            </ErrorBox>
          )}
          {error && <ErrorBox>{error}</ErrorBox>}

          <div className="form-actions">
            <button type="button" className="link-button" onClick={() => showMode('forgot')}>
              Forgot password?
            </button>
            <button type="submit" className="login-button login-submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </div>
        </form>

        <p className="login-fine">
          New here? You don’t need an account to start. <Link to="/">Ask for a plan</Link> and save it when you like it.
        </p>

        <div className="login-footer">
          <span>Don't have an account? </span>
          <Link to={`/signup${window.location.search}`} className="signup-link">Sign up</Link>
        </div>

        <div className="login-footer login-legal">
          <Link to="/terms">Terms</Link>
          {' · '}
          <Link to="/privacy">Privacy</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
